// ===========================================
// Avatar Display Component
// ===========================================
// Displays user avatar (photo or initials fallback)
// Automatically selects the appropriate thumbnail size
//
// IMPORTANT: This is for USER avatars (Admin, Teacher, Parent).
// Students have a SEPARATE avatar system using character sprites.
// ===========================================

import { useState, useMemo } from 'react';
import { Box, Avatar, Skeleton, SxProps, Theme } from '@mui/material';
import {
  AvatarDisplayProps,
  AVATAR_SIZES,
  AvatarSize,
  getThumbnailUrl,
  generateInitials,
} from '../../types/avatar.types';
import { useRoleColorScheme } from '../../hooks/useRoleColorScheme';
import { getContrastTextColor } from '../../styles/brandColors';
import InitialsAvatar from './InitialsAvatar';

// ===========================================
// COMPONENT
// ===========================================

export default function AvatarDisplay({
  avatarInfo,
  firstName = '',
  lastName = '',
  role = 'PARENT',
  size = 'MEDIUM',
  className,
  alt,
  onClick,
}: AvatarDisplayProps) {
  const [imageError, setImageError] = useState(false);

  // Calculate pixel size
  const pixelSize = useMemo(() => {
    return typeof size === 'number' ? size : AVATAR_SIZES[size as AvatarSize];
  }, [size]);

  // Check if user has an uploaded avatar
  const hasAvatar = !!avatarInfo?.avatarUrl;

  // Get the appropriate image URL for the size
  const imageUrl = useMemo(() => {
    if (!hasAvatar || imageError) return null;
    return getThumbnailUrl(avatarInfo, size);
  }, [avatarInfo, hasAvatar, size, imageError]);

  // Generate fallback initials
  const initials = useMemo(() => {
    if (avatarInfo?.initials) {
      return avatarInfo.initials;
    }
    return generateInitials(firstName, lastName);
  }, [avatarInfo, firstName, lastName]);

  // Fallback colours: the server's `roleColor` (the school's colour for the user's real role, #629),
  // else the school's colour for `role`. Text by contrast: white on the yellow Teacher default was
  // unreadable.
  const scheme = useRoleColorScheme();
  const colors = useMemo(() => {
    if (avatarInfo?.roleColor) {
      return {
        background: avatarInfo.roleColor,
        text: getContrastTextColor(avatarInfo.roleColor),
      };
    }
    return scheme[role] ?? scheme.PARENT;
  }, [avatarInfo, role, scheme]);

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  // Container styles
  const containerSx: SxProps<Theme> = {
    width: pixelSize,
    height: pixelSize,
    cursor: onClick ? 'pointer' : 'default',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
  };

  // If we have an avatar image and it hasn't errored
  if (imageUrl && !imageError) {
    return (
      <Avatar
        src={imageUrl}
        alt={alt || `${firstName} ${lastName}`}
        sx={{
          ...containerSx,
          bgcolor: colors.background,
        }}
        className={className}
        onClick={onClick}
        onError={handleImageError}
      >
        {/* Fallback content (shown while loading or on error) */}
        {initials}
      </Avatar>
    );
  }

  // Show initials avatar as fallback
  return (
    <Box
      sx={containerSx}
      className={className}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <InitialsAvatar
        firstName={firstName || avatarInfo?.initials?.charAt(0) || '?'}
        lastName={lastName || avatarInfo?.initials?.charAt(1) || ''}
        role={role}
        size={pixelSize}
      />
    </Box>
  );
}

// ===========================================
// LOADING SKELETON
// ===========================================

export interface AvatarSkeletonProps {
  size?: AvatarSize | number;
}

export function AvatarSkeleton({ size = 'MEDIUM' }: AvatarSkeletonProps) {
  const pixelSize = typeof size === 'number' ? size : AVATAR_SIZES[size as AvatarSize];

  return (
    <Skeleton
      variant="circular"
      width={pixelSize}
      height={pixelSize}
      animation="wave"
    />
  );
}
